<script setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import CompactDateInboxFilters from './components/CompactDateInboxFilters.vue';
import BarChart from 'shared/components/charts/BarChart.vue';
import {
  buildDatasetHoverDimmingHandler,
  buildIntercomLikeBarTooltip,
  removeTooltipEl,
} from 'shared/components/charts/barInteractions';
import telegramAudienceAPI from 'dashboard/routes/dashboard/settings/telegram/api/telegramAudienceAPI';
import telegramReportsAPI from 'dashboard/api/telegramReports';

const store = useStore();
const { t } = useI18n();

const isLoading = ref(false);
const botSettingsLoading = ref(false);
const reportPayload = ref(null);
const botSettings = ref(null);
const generatedAt = ref(null);
const reportError = ref('');
const botSettingsError = ref('');

const selectedBotInboxId = ref(null);
const from = ref(0);
const to = ref(0);
const groupBy = ref('day');

const inboxes = computed(() => store.getters['inboxes/getInboxes'] || []);
const telegramInboxes = computed(() =>
  inboxes.value.filter(inbox => inbox.channel_type === 'Channel::Telegram')
);
const hasBots = computed(() => telegramInboxes.value.length > 0);

const selectedInbox = computed(() =>
  telegramInboxes.value.find(
    inbox => inbox.id === Number(selectedBotInboxId.value)
  )
);

const timeline = computed(() => reportPayload.value?.timeline || []);
const trafficSummary = computed(
  () => reportPayload.value?.summary?.traffic || {}
);
const audienceSummary = computed(
  () => reportPayload.value?.summary?.audience || {}
);
const campaignsSummary = computed(
  () => reportPayload.value?.summary?.campaigns || {}
);
const automationSummary = computed(
  () => reportPayload.value?.summary?.automation || {}
);

const numberFormat = value => Number(value || 0).toLocaleString();
const percentFormat = value => `${Number(value || 0).toFixed(2)}%`;
const generatedAtLabel = computed(() =>
  generatedAt.value
    ? generatedAt.value.toLocaleString()
    : t('LAST_7_DAYS_REPORT.NOT_GENERATED_YET')
);

const botMenuCommands = computed(() => botSettings.value?.menu?.commands || []);
const botPermanentMenuRows = computed(
  () => botSettings.value?.menu?.permanent_menu?.rows || []
);
const botPermanentMenuButtonsCount = computed(() =>
  botPermanentMenuRows.value.reduce(
    (sum, row) => sum + (Array.isArray(row) ? row.length : 0),
    0
  )
);
const botAvatarUrl = computed(() => botSettings.value?.bot_avatar_url || '');

const botMetaLine = computed(() => {
  if (botSettingsLoading.value)
    return t('TELEGRAM_BOT_REPORTS.BOT_SETTINGS.LOADING');
  if (botSettingsError.value) return botSettingsError.value;
  return `${t('TELEGRAM_BOT_REPORTS.BOT_SETTINGS.COMMANDS', {
    count: botMenuCommands.value.length,
  })} · ${t('TELEGRAM_BOT_REPORTS.BOT_SETTINGS.PERMANENT_MENU', {
    count: botPermanentMenuButtonsCount.value,
  })}`;
});

const trafficCards = computed(() => [
  {
    key: 'active-subscribers',
    label: t('TELEGRAM_BOT_REPORTS.METRICS.ACTIVE_SUBSCRIBERS'),
    value: numberFormat(audienceSummary.value.active),
  },
  {
    key: 'new-active-subscribers',
    label: t('TELEGRAM_BOT_REPORTS.METRICS.NEW_ACTIVE_SUBSCRIBERS'),
    value: numberFormat(audienceSummary.value.new_active_in_period),
  },
  {
    key: 'incoming-messages',
    label: t('TELEGRAM_BOT_REPORTS.METRICS.INCOMING_MESSAGES'),
    value: numberFormat(trafficSummary.value.incoming_messages),
  },
  {
    key: 'outgoing-messages',
    label: t('TELEGRAM_BOT_REPORTS.METRICS.OUTGOING_MESSAGES'),
    value: numberFormat(trafficSummary.value.outgoing_messages),
  },
  {
    key: 'conversations-started',
    label: t('TELEGRAM_BOT_REPORTS.METRICS.CONVERSATIONS_STARTED'),
    value: numberFormat(trafficSummary.value.conversations_started),
  },
]);

const operationsCards = computed(() => [
  {
    key: 'campaigns',
    label: t('TELEGRAM_BOT_REPORTS.METRICS.CAMPAIGNS'),
    value: numberFormat(campaignsSummary.value.total_campaigns),
  },
  {
    key: 'campaign-sent',
    label: t('TELEGRAM_BOT_REPORTS.METRICS.CAMPAIGN_SENT'),
    value: numberFormat(campaignsSummary.value.sent_count),
  },
  {
    key: 'campaign-failed',
    label: t('TELEGRAM_BOT_REPORTS.METRICS.CAMPAIGN_FAILED'),
    value: numberFormat(campaignsSummary.value.failed_count),
  },
  {
    key: 'campaign-clicks',
    label: t('TELEGRAM_BOT_REPORTS.METRICS.CAMPAIGN_CLICKS'),
    value: numberFormat(campaignsSummary.value.total_clicks),
  },
  {
    key: 'campaign-ctr',
    label: t('TELEGRAM_BOT_REPORTS.METRICS.CAMPAIGN_CTR'),
    value: percentFormat(campaignsSummary.value.ctr),
  },
  {
    key: 'automation-executions',
    label: t('TELEGRAM_BOT_REPORTS.METRICS.AUTOMATION_EXECUTIONS'),
    value: numberFormat(automationSummary.value.total_executions),
  },
  {
    key: 'automation-success-rate',
    label: t('TELEGRAM_BOT_REPORTS.METRICS.AUTOMATION_SUCCESS_RATE'),
    value: percentFormat(automationSummary.value.success_rate),
  },
  {
    key: 'automation-failed',
    label: t('TELEGRAM_BOT_REPORTS.METRICS.AUTOMATION_FAILED'),
    value: numberFormat(automationSummary.value.failed_count),
  },
]);

const hasTrafficData = computed(() =>
  timeline.value.some(
    p =>
      Number(p.incoming_messages || 0) > 0 ||
      Number(p.outgoing_messages || 0) > 0 ||
      Number(p.new_active_subscribers || 0) > 0
  )
);

const hasOpsData = computed(() =>
  timeline.value.some(
    p =>
      Number(p.campaigns_scheduled || 0) > 0 ||
      Number(p.automation_executions || 0) > 0
  )
);

const trafficChart = computed(() => ({
  labels: timeline.value.map(p => p.date),
  datasets: [
    {
      label: t('TELEGRAM_BOT_REPORTS.CHARTS.INCOMING'),
      data: timeline.value.map(p => p.incoming_messages || 0),
      backgroundColor: '#60A5FA',
      borderRadius: 4,
      categoryPercentage: 0.72,
      barPercentage: 0.95,
    },
    {
      label: t('TELEGRAM_BOT_REPORTS.CHARTS.OUTGOING'),
      data: timeline.value.map(p => p.outgoing_messages || 0),
      backgroundColor: '#34D399',
      borderRadius: 4,
      categoryPercentage: 0.72,
      barPercentage: 0.95,
    },
    {
      label: t('TELEGRAM_BOT_REPORTS.CHARTS.NEW_SUBSCRIBERS'),
      data: timeline.value.map(p => p.new_active_subscribers || 0),
      backgroundColor: '#A78BFA',
      borderRadius: 4,
      categoryPercentage: 0.72,
      barPercentage: 0.95,
    },
  ],
}));

const opsChart = computed(() => ({
  labels: timeline.value.map(p => p.date),
  datasets: [
    {
      label: t('TELEGRAM_BOT_REPORTS.CHARTS.CAMPAIGNS'),
      data: timeline.value.map(p => p.campaigns_scheduled || 0),
      backgroundColor: '#F59E0B',
      borderRadius: 4,
      categoryPercentage: 0.72,
      barPercentage: 0.95,
    },
    {
      label: t('TELEGRAM_BOT_REPORTS.CHARTS.AUTOMATION'),
      data: timeline.value.map(p => p.automation_executions || 0),
      backgroundColor: '#F87171',
      borderRadius: 4,
      categoryPercentage: 0.72,
      barPercentage: 0.95,
    },
  ],
}));

const trafficTooltipId = 'telegram-traffic-tooltip';
const opsTooltipId = 'telegram-ops-tooltip';

const buildChartOptions = (tooltipId, seriesLabels) => ({
  responsive: true,
  maintainAspectRatio: false,
  animation: { duration: 0 },
  interaction: { mode: 'nearest', intersect: true },
  onHover: buildDatasetHoverDimmingHandler(),
  plugins: {
    legend: { display: false },
    tooltip: {
      enabled: false,
      displayColors: false,
      external: buildIntercomLikeBarTooltip({
        tooltipId,
        titleForIndex: index => timeline.value[index]?.date || '',
        seriesLabelForDataset: datasetIndex => seriesLabels[datasetIndex] || '',
        percentDigits: 2,
        showPercent: true,
      }),
    },
  },
  scales: {
    y: {
      beginAtZero: true,
      ticks: {
        callback: value => Number(value).toLocaleString(),
      },
    },
  },
});

const trafficChartOptions = computed(() =>
  buildChartOptions(trafficTooltipId, [
    t('TELEGRAM_BOT_REPORTS.CHARTS.INCOMING'),
    t('TELEGRAM_BOT_REPORTS.CHARTS.OUTGOING'),
    t('TELEGRAM_BOT_REPORTS.CHARTS.NEW_SUBSCRIBERS'),
  ])
);

const opsChartOptions = computed(() =>
  buildChartOptions(opsTooltipId, [
    t('TELEGRAM_BOT_REPORTS.CHARTS.CAMPAIGNS'),
    t('TELEGRAM_BOT_REPORTS.CHARTS.AUTOMATION'),
  ])
);

const fetchReport = async () => {
  if (!selectedBotInboxId.value) return;

  isLoading.value = true;
  reportError.value = '';
  try {
    const response = await telegramReportsAPI.getReport({
      botInboxId: selectedBotInboxId.value,
      from: from.value,
      to: to.value,
      groupBy: groupBy.value,
    });
    reportPayload.value = response?.data?.payload || null;
    generatedAt.value = new Date();
  } catch {
    reportError.value = t('REPORT.DATA_FETCHING_FAILED');
  } finally {
    isLoading.value = false;
  }
};

const fetchBotSettings = async () => {
  if (!selectedBotInboxId.value) return;

  botSettingsLoading.value = true;
  botSettingsError.value = '';
  try {
    const response = await telegramAudienceAPI.getBotSettings({
      botInboxId: selectedBotInboxId.value,
    });
    botSettings.value = response?.data?.payload || null;
  } catch {
    // Keep report usable even if bot settings fail.
    botSettings.value = null;
    botSettingsError.value = t('REPORT.DATA_FETCHING_FAILED');
  } finally {
    botSettingsLoading.value = false;
  }
};

const refreshAll = async () => {
  await Promise.all([fetchReport(), fetchBotSettings()]);
};

const onFilterChange = ({ from: nextFrom, to: nextTo }) => {
  from.value = nextFrom;
  to.value = nextTo;
  if (selectedBotInboxId.value) refreshAll();
};

onMounted(async () => {
  await store.dispatch('inboxes/get');
});

watch(
  telegramInboxes,
  inboxList => {
    if (selectedBotInboxId.value || !inboxList.length) return;
    selectedBotInboxId.value = inboxList[0].id;
  },
  { immediate: true }
);

watch(selectedBotInboxId, () => {
  if (from.value) refreshAll();
});

onBeforeUnmount(() => {
  removeTooltipEl(trafficTooltipId);
  removeTooltipEl(opsTooltipId);
});
</script>

<template>
  <div class="tgb-page">
    <div class="tgb-shell">
      <div class="tgb-header">
        <div class="tgb-header-left">
          <h1 class="tgb-title">{{ $t('TELEGRAM_BOT_REPORTS.HEADER') }}</h1>
        </div>
      </div>

      <div class="tgb-toolbar">
        <div class="tgb-toolbar-row">
          <CompactDateInboxFilters
            :show-add-filter="false"
            :show-inbox="false"
            @filter-change="onFilterChange"
          />
          <div v-if="hasBots" class="tgb-toolbar-actions">
            <label class="tgb-field">
              <span class="tgb-label">{{
                $t('TELEGRAM_BOT_REPORTS.FILTERS.BOT')
              }}</span>
              <select v-model="selectedBotInboxId" class="tgb-select">
                <option
                  v-for="inbox in telegramInboxes"
                  :key="inbox.id"
                  :value="inbox.id"
                >
                  {{ inbox.name }}
                </option>
              </select>
            </label>
            <button
              class="tgb-action-btn"
              type="button"
              :disabled="isLoading"
              @click="refreshAll"
            >
              {{ $t('TELEGRAM_BOT_REPORTS.ACTIONS.REFRESH') }}
            </button>
          </div>
        </div>
      </div>

      <div class="tgb-content">
        <div v-if="reportError" class="tgb-error">
          {{ reportError }}
        </div>

        <section v-if="!hasBots" class="tgb-card tgb-empty">
          <div class="tgb-empty-title">
            {{ $t('TELEGRAM_BOT_REPORTS.EMPTY_STATE.TITLE') }}
          </div>
          <div class="tgb-empty-desc">
            {{ $t('TELEGRAM_BOT_REPORTS.EMPTY_STATE.DESCRIPTION') }}
          </div>
        </section>

        <template v-else>
          <section class="tgb-card">
            <div class="tgb-card-header">
              <div class="tgb-card-title-wrap">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                  width="20"
                  height="20"
                >
                  <path
                    stroke="currentColor"
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M12 8h.01M11 12h1v4h1M3 12a9 9 0 1 0 18.001 0A9 9 0 0 0 3 12"
                  />
                </svg>
                <div>
                  <h2 class="tgb-card-title">
                    {{
                      selectedInbox?.name || $t('TELEGRAM_BOT_REPORTS.HEADER')
                    }}
                  </h2>
                  <p class="tgb-card-subtitle">
                    Telegram inbox performance overview
                  </p>
                </div>
              </div>
              <div class="tgb-card-meta">{{ botMetaLine }}</div>
            </div>

            <div class="tgb-bot-panel">
              <div class="tgb-bot-avatar">
                <img
                  v-if="botAvatarUrl"
                  :src="botAvatarUrl"
                  :alt="selectedInbox?.name"
                  class="tgb-bot-avatar-img"
                />
                <div v-else class="tgb-bot-avatar-fallback">
                  <span class="i-lucide-bot size-5" />
                </div>
              </div>
              <div class="tgb-bot-info">
                <div class="tgb-bot-name">{{ selectedInbox?.name }}</div>
                <div class="tgb-bot-sub">Channel: Telegram</div>
              </div>
              <div class="tgb-bot-stats">
                <div class="tgb-bot-stat">
                  <span class="tgb-bot-stat__label">Commands</span>
                  <span class="tgb-bot-stat__value">
                    {{ numberFormat(botMenuCommands.length) }}
                  </span>
                </div>
                <div class="tgb-bot-stat">
                  <span class="tgb-bot-stat__label">Permanent menu</span>
                  <span class="tgb-bot-stat__value">
                    {{ numberFormat(botPermanentMenuButtonsCount) }}
                  </span>
                </div>
              </div>
            </div>
            <div v-if="botSettingsError" class="tgb-settings-error">
              {{ botSettingsError }}
            </div>
          </section>

          <woot-loading-state
            v-if="isLoading"
            class="tgb-loading"
            :message="$t('REPORT.LOADING_CHART')"
          />

          <template v-else>
            <section class="tgb-card">
              <div class="tgb-card-header">
                <div class="tgb-card-title-wrap">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 3v18h18M7 14l3-3 3 2 4-5"
                    />
                  </svg>
                  <div>
                    <h2 class="tgb-card-title">
                      {{ $t('TELEGRAM_BOT_REPORTS.SECTIONS.TRAFFIC') }}
                    </h2>
                    <p class="tgb-card-subtitle">
                      Subscribers, messages, and conversation growth over the
                      selected period.
                    </p>
                  </div>
                </div>
              </div>
              <div class="tgb-metrics">
                <div
                  v-for="card in trafficCards"
                  :key="card.key"
                  class="tgb-metric"
                >
                  <div class="tgb-metric-label">{{ card.label }}</div>
                  <div class="tgb-metric-value">{{ card.value }}</div>
                </div>
              </div>
              <div class="tgb-chart">
                <div v-if="!hasTrafficData" class="tgb-chart-empty">
                  No traffic data for the selected range.
                </div>
                <BarChart
                  v-else
                  :collection="trafficChart"
                  :chart-options="trafficChartOptions"
                />
              </div>
            </section>

            <section class="tgb-card">
              <div class="tgb-card-header">
                <div class="tgb-card-title-wrap">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 3v18m9-9H3"
                    />
                  </svg>
                  <div>
                    <h2 class="tgb-card-title">
                      {{ $t('TELEGRAM_BOT_REPORTS.SECTIONS.OPERATIONS') }}
                    </h2>
                    <p class="tgb-card-subtitle">
                      Campaign and automation health in one operational
                      overview.
                    </p>
                  </div>
                </div>
              </div>
              <div class="tgb-metrics">
                <div
                  v-for="card in operationsCards"
                  :key="card.key"
                  class="tgb-metric"
                >
                  <div class="tgb-metric-label">{{ card.label }}</div>
                  <div class="tgb-metric-value">{{ card.value }}</div>
                </div>
              </div>
              <div class="tgb-chart">
                <div v-if="!hasOpsData" class="tgb-chart-empty">
                  No operations data for the selected range.
                </div>
                <BarChart
                  v-else
                  :collection="opsChart"
                  :chart-options="opsChartOptions"
                />
              </div>
            </section>

            <section class="tgb-card">
              <div class="tgb-card-header">
                <div class="tgb-card-title-wrap">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M3 7h18M3 12h18M3 17h18"
                    />
                  </svg>
                  <div>
                    <h2 class="tgb-card-title">
                      {{ $t('TELEGRAM_BOT_REPORTS.SECTIONS.CAMPAIGNS') }}
                    </h2>
                    <p class="tgb-card-subtitle">
                      Latest campaign outcomes with send, fail, click, and CTR
                      performance.
                    </p>
                  </div>
                </div>
              </div>
              <div class="tgb-table-wrap">
                <div
                  v-if="!(reportPayload?.campaigns?.items || []).length"
                  class="tgb-table-empty"
                >
                  {{ $t('TELEGRAM_BOT_REPORTS.TABLE.NO_CAMPAIGNS') }}
                </div>
                <table v-else class="tgb-table">
                  <thead>
                    <tr>
                      <th>{{ $t('TELEGRAM_BOT_REPORTS.TABLE.CAMPAIGN') }}</th>
                      <th>{{ $t('TELEGRAM_BOT_REPORTS.TABLE.STATUS') }}</th>
                      <th>
                        {{ $t('TELEGRAM_BOT_REPORTS.TABLE.SCHEDULED_AT') }}
                      </th>
                      <th class="tgb-num-col">
                        {{ $t('TELEGRAM_BOT_REPORTS.TABLE.SENT') }}
                      </th>
                      <th class="tgb-num-col">
                        {{ $t('TELEGRAM_BOT_REPORTS.TABLE.FAILED') }}
                      </th>
                      <th class="tgb-num-col">
                        {{ $t('TELEGRAM_BOT_REPORTS.TABLE.CLICKS') }}
                      </th>
                      <th class="tgb-num-col">
                        {{ $t('TELEGRAM_BOT_REPORTS.TABLE.CTR') }}
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr
                      v-for="item in reportPayload.campaigns.items"
                      :key="item.id"
                    >
                      <td>
                        <router-link
                          :to="{
                            name: 'campaigns_telegram_report',
                            params: {
                              accountId: $route.params.accountId,
                              id: item.id,
                            },
                          }"
                          class="tgb-link"
                        >
                          {{ item.title }}
                        </router-link>
                      </td>
                      <td>
                        <span class="tgb-badge">{{
                          item.campaign_status
                        }}</span>
                      </td>
                      <td>{{ item.scheduled_at || '--' }}</td>
                      <td class="tgb-num-col">
                        {{ numberFormat(item.analytics?.sent_count) }}
                      </td>
                      <td class="tgb-num-col">
                        {{ numberFormat(item.analytics?.failed_count) }}
                      </td>
                      <td class="tgb-num-col">
                        {{ numberFormat(item.analytics?.total_clicks) }}
                      </td>
                      <td class="tgb-num-col">
                        {{ percentFormat(item.analytics?.ctr) }}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            <section class="tgb-card">
              <div class="tgb-card-header">
                <div class="tgb-card-title-wrap">
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    width="20"
                    height="20"
                  >
                    <path
                      stroke="currentColor"
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M12 6v6l4 2"
                    />
                  </svg>
                  <div>
                    <h2 class="tgb-card-title">
                      {{ $t('TELEGRAM_BOT_REPORTS.SECTIONS.AUTOMATION') }}
                    </h2>
                    <p class="tgb-card-subtitle">
                      Top flows and recent node failures to monitor automation
                      stability.
                    </p>
                  </div>
                </div>
              </div>
              <div class="tgb-split">
                <div class="tgb-pane">
                  <div class="tgb-sub-title">
                    {{ $t('TELEGRAM_BOT_REPORTS.TABLE.TOP_FLOWS') }}
                  </div>
                  <div
                    v-if="!(reportPayload?.automation?.top_flows || []).length"
                    class="tgb-table-empty"
                  >
                    {{ $t('TELEGRAM_BOT_REPORTS.TABLE.NO_FLOWS') }}
                  </div>
                  <div v-else class="tgb-table-wrap">
                    <table class="tgb-table">
                      <thead>
                        <tr>
                          <th>{{ $t('TELEGRAM_BOT_REPORTS.TABLE.FLOW') }}</th>
                          <th class="tgb-num-col">
                            {{ $t('TELEGRAM_BOT_REPORTS.TABLE.EXECUTIONS') }}
                          </th>
                          <th class="tgb-num-col">
                            {{ $t('TELEGRAM_BOT_REPORTS.TABLE.FAILED') }}
                          </th>
                          <th class="tgb-num-col">
                            {{ $t('TELEGRAM_BOT_REPORTS.TABLE.SUCCESS_RATE') }}
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr
                          v-for="flow in reportPayload.automation.top_flows"
                          :key="flow.id"
                        >
                          <td>
                            <router-link
                              :to="{
                                name: 'automation_flows_edit',
                                params: {
                                  accountId: $route.params.accountId,
                                  flowId: flow.id,
                                },
                              }"
                              class="tgb-link"
                            >
                              {{ flow.name || `#${flow.id}` }}
                            </router-link>
                            <div class="tgb-flow-sub">
                              {{ flow.trigger_type }}
                            </div>
                          </td>
                          <td class="tgb-num-col">
                            {{ numberFormat(flow.executions) }}
                          </td>
                          <td class="tgb-num-col">
                            {{ numberFormat(flow.failed_count) }}
                          </td>
                          <td class="tgb-num-col">
                            {{ percentFormat(flow.success_rate) }}
                          </td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </div>

                <div class="tgb-pane">
                  <div class="tgb-sub-title">
                    {{ $t('TELEGRAM_BOT_REPORTS.TABLE.RECENT_ERRORS') }}
                  </div>
                  <div
                    v-if="
                      !(reportPayload?.automation?.recent_node_errors || [])
                        .length
                    "
                    class="tgb-table-empty"
                  >
                    {{ $t('TELEGRAM_BOT_REPORTS.TABLE.NO_ERRORS') }}
                  </div>
                  <div v-else class="tgb-error-list">
                    <div
                      v-for="err in reportPayload.automation.recent_node_errors"
                      :key="err.id"
                      class="tgb-error-item"
                    >
                      <div class="tgb-error-title">
                        {{
                          err.automation_flow_name ||
                          `#${err.automation_flow_id}`
                        }}
                        <span class="tgb-error-node">· {{ err.node_id }}</span>
                      </div>
                      <div class="tgb-error-message">
                        {{ err.error_message || '--' }}
                      </div>
                      <div class="tgb-error-meta">
                        {{ err.executed_at || '--' }}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </section>

            <p class="tgb-footer-note">
              This report was generated on {{ generatedAtLabel }}.
            </p>
          </template>
        </template>
      </div>
    </div>
  </div>
</template>

<style scoped>
.tgb-page {
  min-height: 100vh;
  min-height: 100dvh;
  width: 100%;
  box-sizing: border-box;
  background: #ffffff;
  color: #111827;
}
.tgb-shell {
  min-width: 650px;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #ffffff;
  overflow: visible;
}

.tgb-header {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  min-height: 58px;
  padding: 0 24px;
  border-bottom: 1px solid #eceff3;
  position: sticky;
  top: 0;
  z-index: 50;
  background: #ffffff;
}

.tgb-header-left {
  display: flex;
  align-items: center;
  gap: 0;
}
.tgb-title {
  margin: 0;
  font-size: 18px !important;
  line-height: 24px;
  font-weight: 600;
  letter-spacing: -0.02em;
  color: #111827;
}
.tgb-toolbar {
  padding: 12px 24px 8px;
  background: #ffffff;
  box-shadow: rgba(0, 0, 0, 0.3) 0 3px 10px -6px;
  position: sticky;
  top: 58px;
  z-index: 49;
}

.tgb-toolbar-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
  flex-wrap: wrap;
}
.tgb-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.tgb-field {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.tgb-label {
  font-size: 11px;
  font-weight: 600;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.03em;
}
.tgb-select {
  height: 34px;
  min-width: 220px;
  border-radius: 8px;
  border: 1px solid #d9dde3;
  background: #f3f4f6;
  color: #111827;
  font-size: 13px;
  font-weight: 600;
  padding: 0 10px;
  cursor: pointer;
}
.tgb-action-btn {
  height: 34px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #111827;
  padding: 0 14px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  white-space: nowrap;
}
.tgb-action-btn:hover:not(:disabled) {
  background: #f9fafb;
}
.tgb-action-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.tgb-content {
  padding: 16px 32px;
  display: flex;
  flex-direction: column;
  gap: 20px;
}

.tgb-error {
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
}
.tgb-card {
  border-radius: 4px;
  border: 1px solid #d8dee5;
  background: #ffffff;
  overflow: hidden;
}
.tgb-card-header {
  padding: 18px 20px;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.tgb-card-title-wrap {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  min-width: 0;
}
.tgb-card-title {
  margin: 0;
  color: #111827;
  font-size: 16px;
  font-weight: 600;
  line-height: 1.3;
}
.tgb-card-subtitle {
  margin: 4px 0 0;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}
.tgb-card-meta {
  font-size: 12px;
  color: #6b7280;
  white-space: nowrap;
}

.tgb-settings-error {
  margin: 0 20px 16px;
  border: 1px solid #fecaca;
  background: #fef2f2;
  color: #b91c1c;
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 12px;
}

.tgb-bot-panel {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 20px 20px;
}
.tgb-bot-avatar {
  width: 42px;
  height: 42px;
  border-radius: 999px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}
.tgb-bot-avatar-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.tgb-bot-avatar-fallback {
  color: #6b7280;
}
.tgb-bot-info {
  min-width: 0;
  flex: 1 1 auto;
}
.tgb-bot-name {
  font-weight: 700;
  color: #111827;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.tgb-bot-sub {
  margin-top: 2px;
  font-size: 12px;
  color: #6b7280;
}
.tgb-bot-stats {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
}
.tgb-bot-stat {
  min-width: 120px;
  padding: 10px 12px;
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
}
.tgb-bot-stat__label {
  display: block;
  font-size: 12px;
  color: #6b7280;
  font-weight: 600;
}
.tgb-bot-stat__value {
  display: block;
  margin-top: 4px;
  font-size: 20px;
  font-weight: 700;
  color: #111827;
}

.tgb-loading {
  padding: 40px 0;
}

.tgb-metrics {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(220px, 1fr));
  gap: 12px;
  padding: 16px 20px 0;
}
.tgb-metric {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #f8fafc;
  padding: 12px;
}
.tgb-metric-label {
  font-size: 13px;
  font-weight: 600;
  color: #111827;
}
.tgb-metric-value {
  margin-top: 10px;
  font-size: 28px;
  line-height: 32px;
  font-weight: 700;
  color: #111827;
}

.tgb-chart {
  height: 320px;
  padding: 16px 20px 20px;
}
.tgb-chart-empty {
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #6b7280;
  font-size: 13px;
}

.tgb-table-wrap {
  overflow: auto;
}
.tgb-table-empty {
  font-size: 13px;
  color: #6b7280;
  padding: 20px;
}
.tgb-table {
  width: 100%;
  min-width: 760px;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}
.tgb-table th {
  padding: 10px 12px;
  text-align: left;
  font-weight: 700;
  color: #111827;
  border-bottom: 1px solid #e5e7eb;
  background: #f8fafc;
  white-space: nowrap;
}
.tgb-table td {
  padding: 12px;
  border-bottom: 1px solid #edf1f5;
  vertical-align: middle;
  color: #111827;
}
.tgb-num-col {
  text-align: right !important;
  white-space: nowrap;
}

.tgb-link {
  color: #111827;
  font-weight: 700;
  text-decoration: underline;
  text-underline-position: from-font;
}
.tgb-flow-sub {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
}
.tgb-badge {
  display: inline-flex;
  align-items: center;
  padding: 2px 8px;
  border-radius: 999px;
  font-size: 12px;
  background: rgba(99, 102, 241, 0.12);
  color: #111827;
  border: 1px solid rgba(99, 102, 241, 0.2);
}

.tgb-split {
  display: grid;
  grid-template-columns: 1fr;
  gap: 14px;
  padding: 16px 20px 20px;
}
.tgb-pane {
  border: 1px solid #e5e7eb;
  border-radius: 10px;
  background: #ffffff;
  overflow: hidden;
}
.tgb-sub-title {
  font-weight: 700;
  color: #111827;
  margin: 0;
  padding: 14px 16px;
  border-bottom: 1px solid #edf1f5;
  background: #f8fafc;
}

.tgb-error-list {
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding: 16px;
}
.tgb-error-item {
  padding: 10px 12px;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
  background: #f8fafc;
}
.tgb-error-title {
  font-weight: 700;
  color: #111827;
  font-size: 12px;
}
.tgb-error-node {
  font-weight: 500;
  color: #6b7280;
}
.tgb-error-message {
  margin-top: 6px;
  font-size: 12px;
  color: #111827;
  word-break: break-word;
}
.tgb-error-meta {
  margin-top: 6px;
  font-size: 11px;
  color: #6b7280;
}

.tgb-empty {
  padding: 24px;
}
.tgb-empty-title {
  font-weight: 700;
  color: #111827;
}
.tgb-empty-desc {
  margin-top: 6px;
  font-size: 12px;
  color: #6b7280;
}
.tgb-footer-note {
  margin-top: 4px;
  font-size: 12px;
  color: #6b7280;
  line-height: 1.5;
}

@media (min-width: 1024px) {
  .tgb-split {
    grid-template-columns: 1.2fr 1fr;
  }
}

@media (max-width: 1100px) {
  .tgb-content {
    padding: 12px 16px 16px;
  }
  .tgb-toolbar {
    padding: 10px 16px 8px;
  }
}

@media (max-width: 920px) {
  .tgb-toolbar-actions {
    justify-content: flex-start;
  }
  .tgb-bot-panel {
    flex-wrap: wrap;
  }
}

::global(body.dark) .tgb-page,
::global(body.dark) .tgb-shell,
::global(body.dark) .tgb-header,
::global(body.dark) .tgb-toolbar,
::global(body.dark) .tgb-content,
::global(body.dark) .tgb-card,
::global(body.dark) .tgb-pane,
::global(body.dark) .tgb-table th,
::global(body.dark) .tgb-table td,
::global(body.theme-mix) .tgb-page,
::global(body.theme-mix) .tgb-shell,
::global(body.theme-mix) .tgb-header,
::global(body.theme-mix) .tgb-toolbar,
::global(body.theme-mix) .tgb-content,
::global(body.theme-mix) .tgb-card,
::global(body.theme-mix) .tgb-pane,
::global(body.theme-mix) .tgb-table th,
::global(body.theme-mix) .tgb-table td {
  background: #ffffff !important;
  color: #111827 !important;
}

::global(body.dark) .tgb-table th *,
::global(body.theme-mix) .tgb-table th * {
  color: #111827 !important;
}
</style>
